import React from "react";
import { Badge } from "@/components/ui/badge";
import { Clock } from "lucide-react";
import { computeSegmentDuration, formatDurationMinutes } from "@/utils/segmentDuration";
import { formatDistance } from "@/utils/distance";

/**
 * Small pill showing estimated travel duration (e.g. "⏱ 2h 15m") for transit
 * segments (flights, trains, buses, cruises, rideshares). Returns null for
 * segments where duration is irrelevant or can't be computed.
 *
 * For flights, if the IATA→timezone map isn't available yet (or one of the
 * airports is missing), we fall back to naive math and append a subtle "~"
 * prefix so the number isn't presented as authoritative across timezones.
 *
 * For car_rentals we surface the *cumulative driving time* across the road
 * trip's legs (Mapbox-computed, persisted on the segment as
 * `road_trip_route.total_duration_sec`). Only renders when the trip has been
 * routed — older rentals with no cached route stay quiet.
 */
const SegmentDurationBadge = ({ segment, className = "" }) => {
  if ((segment?.type || "").toLowerCase() === "car_rental") {
    const route = segment.road_trip_route;
    const sec = route?.total_duration_sec;
    const label = sec > 0 ? formatDurationMinutes(sec / 60) : null;
    if (!label) return null;
    const dist = formatDistance(route?.total_distance_m);
    return (
      <Badge
        variant="outline"
        title={`Estimated drive time across road-trip legs${dist ? ` · ${dist}` : ""}`}
        data-testid={`segment-duration-badge-${segment.id}`}
        className={`flex-shrink-0 text-[10px] px-1.5 py-0 bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800 inline-flex items-center gap-1 ${className}`}
      >
        <Clock className="h-3 w-3" />
        <span>{label}{dist ? ` · ${dist}` : ""}</span>
      </Badge>
    );
  }

  const result = computeSegmentDuration(segment);
  if (!result || !result.formatted) return null;

  const isFlight = (segment?.type || "").toLowerCase() === "flight";
  const label = isFlight && !result.tzAware ? `~${result.formatted}` : result.formatted;
  const tooltip = isFlight && !result.tzAware
    ? "Approximate (timezones not yet loaded)"
    : "Estimated duration of travel";

  return (
    <Badge
      variant="outline"
      title={tooltip}
      data-testid={`segment-duration-badge-${segment.id}`}
      className={`flex-shrink-0 text-[10px] px-1.5 py-0 bg-gray-50 dark:bg-gray-800/60 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 inline-flex items-center gap-1 ${className}`}
    >
      <Clock className="h-3 w-3" />
      <span>{label}</span>
    </Badge>
  );
};

export default SegmentDurationBadge;
